// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Pencil, Clock, MapPin, User } from 'lucide-react';
import MatchEditDialog from './MatchEditDialog';
import { formatTime } from '@/lib/dates';

interface ScheduleListViewProps {
  tournamentId: string | undefined;
  courts: any[];
  referees: any[];
}

const ScheduleListView: React.FC<ScheduleListViewProps> = ({
  tournamentId,
  courts,
  referees
}) => {
  const [matches, setMatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingMatch, setEditingMatch] = useState<any>(null);

  const fetchMatches = async () => {
    if (!tournamentId) {
      console.log('[ScheduleListView] No tournamentId, skipping fetch');
      setLoading(false);
      return;
    }
    
    console.log('[ScheduleListView] Fetching matches for tournamentId:', tournamentId);
    
    // Fetch scheduled matches
    const { data: allMatches, error: matchError } = await supabase
      .from('tournament_matches')
      .select('*')
      .eq('tournament_id', tournamentId)
      .not('scheduled_time', 'is', null)
      .order('scheduled_time', { ascending: true })
      .order('round_number', { ascending: true })
      .order('match_number', { ascending: true });

    if (matchError) {
      console.error('[ScheduleListView] Query error:', matchError);
      setMatches([]);
      setLoading(false);
      return;
    }

    console.log('[ScheduleListView] Scheduled matches found:', allMatches?.length);
    
    if (!allMatches || allMatches.length === 0) {
      setMatches([]);
      setLoading(false);
      return;
    }

    // Get unique bracket IDs (team1_id and team2_id reference tournament_brackets.id)
    const bracketIds = [...new Set(
      allMatches.flatMap(m => [m.team1_id, m.team2_id].filter(Boolean))
    )];
    
    // Fetch team names from tournament_brackets (which has team_name directly)
    let bracketsMap: Record<string, string> = {};
    if (bracketIds.length > 0) {
      const { data: brackets, error: bracketsError } = await supabase
        .from('tournament_brackets')
        .select('id, team_name')
        .in('id', bracketIds);
      
      if (bracketsError) {
        console.error('[ScheduleListView] Brackets query error:', bracketsError);
      } else {
        bracketsMap = (brackets || []).reduce((acc, b) => ({ 
          ...acc, 
          [b.id]: b.team_name 
        }), {});
      }
    }
    
    // Enrich matches with team names from brackets
    const enrichedMatches = allMatches.map(m => ({
      ...m,
      team1: m.team1_id ? { id: m.team1_id, name: bracketsMap[m.team1_id] || 'TBD' } : null,
      team2: m.team2_id ? { id: m.team2_id, name: bracketsMap[m.team2_id] || 'TBD' } : null,
    }));

    console.log('[ScheduleListView] Enriched matches:', enrichedMatches.length);
    
    setMatches(enrichedMatches);
    setLoading(false);
  };

  useEffect(() => {
    fetchMatches();
  }, [tournamentId]);

  const formatDateTime = (dateTime: string) => {
    if (!dateTime) return 'Not scheduled';
    const date = new Date(dateTime);
    const datePart = date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    return `${datePart}, ${formatTime(date)}`;
  };

  // Helper to get pool letter from pool_id (e.g., "pool_A" -> "A")
  const getPoolLetter = (poolId: string) => {
    if (!poolId) return '';
    return poolId.replace('pool_', '').toUpperCase();
  };

  // Helper to get round label for playoff rounds
  const getPlayoffRoundLabel = (roundNumber: number, totalRounds: number, bracketType: string) => {
    if (bracketType === 'final') return 'Championship';
    if (bracketType === 'third_place') return '3rd Place Match';
    
    const roundsFromEnd = totalRounds - roundNumber;
    if (roundsFromEnd === 0) return 'Finals';
    if (roundsFromEnd === 1) return 'Semi-Finals';
    if (roundsFromEnd === 2) return 'Quarter-Finals';
    return `Round of ${Math.pow(2, roundsFromEnd + 1)}`;
  };

  // Separate pool and playoff matches
  const poolMatches = matches.filter(m => m.bracket_type === 'pool');
  const playoffMatches = matches.filter(m => m.bracket_type !== 'pool');

  // Group pool matches by pool_id
  const matchesByPool = poolMatches.reduce((acc, match) => {
    const poolId = match.pool_id || 'unknown';
    if (!acc[poolId]) acc[poolId] = [];
    acc[poolId].push(match);
    return acc;
  }, {} as Record<string, any[]>);

  // Group playoff matches by round_number
  const matchesByRound = playoffMatches.reduce((acc, match) => {
    const round = match.round_number || 0;
    if (!acc[round]) acc[round] = [];
    acc[round].push(match);
    return acc;
  }, {} as Record<number, any[]>);

  // Get max round for playoff label calculation
  const maxPlayoffRound = Math.max(...playoffMatches.map(m => m.round_number || 0), 0);

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading schedule...</div>;
  }

  if (!tournamentId) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No tournament selected.
      </div>
    );
  }

  if (matches.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No matches scheduled yet. Generate brackets first, then use Auto-Schedule to assign times.
      </div>
    );
  }

  const renderMatchCard = (match: any) => (
    <div
      key={match.id}
      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
    >
      <div className="flex-1">
        <div className="font-medium">
          {match.team1?.name || 'TBD'} vs {match.team2?.name || 'TBD'}
        </div>
        <div className="flex flex-wrap gap-3 mt-1 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatDateTime(match.scheduled_time)}
          </span>
          {match.court_field && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {match.court_field}
            </span>
          )}
          {match.referee && (
            <span className="flex items-center gap-1">
              <User className="w-3 h-3" />
              {match.referee.name}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant={match.status === 'completed' ? 'default' : 'secondary'}>
          {match.status}
        </Badge>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setEditingMatch(match)}
        >
          <Pencil className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Pool Play Section */}
      {Object.keys(matchesByPool).length > 0 && (
        <>
          <h3 className="text-lg font-semibold text-foreground">Pool Play</h3>
          {Object.entries(matchesByPool)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([poolId, poolMatchList]) => (
              <Card key={poolId}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Badge variant="outline" className="text-base">
                      Pool {getPoolLetter(poolId)}
                    </Badge>
                    <span className="text-sm text-muted-foreground font-normal">
                      ({(poolMatchList as any[]).length} matches)
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(poolMatchList as any[]).map(renderMatchCard)}
                  </div>
                </CardContent>
              </Card>
            ))}
        </>
      )}

      {/* Playoff Section */}
      {Object.keys(matchesByRound).length > 0 && (
        <>
          <h3 className="text-lg font-semibold text-foreground">Playoffs</h3>
          {Object.entries(matchesByRound)
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([round, roundMatches]) => {
              const firstMatch = roundMatches[0];
              const roundLabel = getPlayoffRoundLabel(
                Number(round), 
                maxPlayoffRound, 
                firstMatch?.bracket_type || 'winners'
              );
              
              return (
                <Card key={round}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">{roundLabel}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {(roundMatches as any[]).map(renderMatchCard)}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
        </>
      )}

      <MatchEditDialog
        open={!!editingMatch}
        onOpenChange={(open) => !open && setEditingMatch(null)}
        match={editingMatch}
        courts={courts}
        referees={referees}
        onSave={fetchMatches}
      />
    </div>
  );
};

export default ScheduleListView;
